import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api, formatPrice } from "../lib/api";
import { useI18n } from "../lib/i18n";

/**
 * Header search by artist / title / label / catalogue number. Shows live results while typing;
 * Enter (or "Ver todos") opens the catalogue filtered by the query.
 */
export default function SearchBox({ onNavigate, autoFocus = false, className = "" }) {
  const { lang } = useI18n();
  const tx = (es, en) => (lang === "en" ? en : es);
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setTotal(0);
      return undefined;
    }
    setLoading(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/listings", { params: { q: term, per_page: 6 } });
        if (!cancelled) {
          setResults(data.items || []);
          setTotal(data.pagination?.total || 0);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const goToCatalog = () => {
    const term = q.trim();
    if (!term) return;
    navigate(`/catalogo?q=${encodeURIComponent(term)}`);
    setOpen(false);
    setQ("");
    onNavigate?.();
  };

  const done = () => {
    setOpen(false);
    setQ("");
    onNavigate?.();
  };

  const showPanel = open && q.trim().length >= 2;

  return (
    <div ref={boxRef} className={`relative ${className}`} data-testid="search-box">
      <form
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          goToCatalog();
        }}
        className="flex items-center gap-2 border border-border focus-within:border-foreground px-2.5 py-1.5 bg-background"
      >
        <Search size={14} className="text-muted-foreground shrink-0" aria-hidden="true" />
        <input
          type="search"
          value={q}
          autoFocus={autoFocus}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
          placeholder={tx("Buscar artista, título, sello…", "Search artist, title, label…")}
          aria-label={tx("Buscar en el catálogo", "Search the catalogue")}
          className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
          data-testid="search-input"
        />
        {q && (
          <button type="button" onClick={() => setQ("")} aria-label={tx("Borrar", "Clear")} className="text-muted-foreground hover:text-foreground">
            <X size={13} />
          </button>
        )}
      </form>

      {showPanel && (
        <div className="absolute right-0 left-0 md:left-auto md:w-[380px] mt-1 bg-background border border-border shadow-lg z-50" data-testid="search-results">
          {loading && results.length === 0 ? (
            <p className="p-4 text-xs text-muted-foreground">{tx("Buscando…", "Searching…")}</p>
          ) : results.length === 0 ? (
            <p className="p-4 text-xs text-muted-foreground">{tx("Sin resultados para", "No results for")} “{q.trim()}”.</p>
          ) : (
            <>
              <ul className="divide-y divide-border max-h-[60vh] overflow-y-auto">
                {results.map((it) => (
                  <li key={it.listing_id}>
                    <Link to={`/catalogo/${it.slug}`} onClick={done} className="flex items-center gap-3 p-2.5 hover:bg-[hsl(var(--muted))]">
                      <img src={it.thumb || it.cover_image || "/brand/cover-placeholder.png"} alt="" className="w-10 h-10 object-cover bg-[hsl(var(--muted))] shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block text-xs text-muted-foreground truncate">{it.artist}</span>
                        <span className="block text-sm truncate">{it.title}</span>
                      </span>
                      <span className="text-xs tabular-nums whitespace-nowrap">{formatPrice(it.price, it.currency)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <button type="button" onClick={goToCatalog} className="w-full text-left p-3 text-xs border-t border-border hover:bg-[hsl(var(--muted))]" data-testid="search-see-all">
                {tx(`Ver los ${total} resultados en el catálogo →`, `See all ${total} results in the catalogue →`)}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
